// frontend/src/components/cardOnFile/trialOfferStripView.js

import { formatDiscountPercent } from "../upgradeOfferText";
import { isInvoiceBilledAccount } from "../../utils/accountAccess";
import { OFFER_KIND, resolveOfferView } from "../../utils/friendCodeOffer";
import { isCardOnFileLive, resolveCardOnFileView } from "./cardOnFileView";

/**
 * The display model of the dashboard's permanent trial strip and of the
 * "the discount drops daily" line (round 2 of card on file), and the
 * whole-days-left count the 1-3 day urgent banner reads.
 *
 * READ, NEVER FETCHED, NEVER COMPUTED. Pure functions over the one offer
 * payload `useUpgradeOffer` already shares: today's percent is the server's
 * `discount_percent`, "does it still drop" is its `next_discount_percent`
 * (read as a gate only - its figure is never shown, ROUND 8), and "is this
 * the offer's last day" is its `is_last_day`, and "on which Israeli day does
 * the trial end" its `trial_calendar_days_left` (review R13F-1 / R13F-2).
 * Nothing here steps a ladder, and the device clock is read only as
 * `resolveTrialDaysLeft`'s fallback (the banner's) - display == charge, so the
 * strip can only state a number the checkout will actually charge.
 *
 * FAIL-CLOSED. Anything unexpected (no offer, an offer of another kind, a
 * feature that is off, a missing "tomorrow" figure outside the last day)
 * answers null and the surface renders nothing - never a half sentence.
 */

/**
 * The strip's states: F1 (pay now), F2 (save card), F3 (the last day) and,
 * ROUND 13, ENDING - F1 in the 1-3 days before the last one, its headline the
 * trial-ending countdown. There is no "card saved" state (ROUND 4): a user who
 * holds a scheduled first charge sees no strip at all.
 */
export const STRIP_STATE = Object.freeze({
  PAY_NOW: "pay_now",
  ENDING: "ending",
  SAVE_CARD: "save_card",
  LAST_DAY: "last_day",
});

/** The states that ARE the early-upgrade offer (F1, its ending days, F3):
 *  pay now, nothing to open (ROUND 12), no 1-3 day banner under them
 *  (ROUND 13). F2 is the one state that is not. */
const PAY_NOW_STATES = new Set([STRIP_STATE.PAY_NOW, STRIP_STATE.ENDING, STRIP_STATE.LAST_DAY]);

/** Is this strip a pay-now strip (see `PAY_NOW_STATES`)? */
export const isPayNowStrip = (strip) => Boolean(strip && PAY_NOW_STATES.has(strip.state));

/** The days before the last one in which the trial-ending wording runs - the
 *  same window as the dashboard's urgent banner (`days > 0 && days <= 3`),
 *  counted in Israeli calendar days (`readTrialCalendarDaysLeft`). */
export const ENDING_WINDOW_DAYS = 3;

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Whole days left in the trial - the 1-3 day urgent banner's count
 * (`SubscriptionBanner.getBannerConfig`). NOT the strip's: its "today" /
 * "tomorrow" wording keys on `readTrialCalendarDaysLeft` (a 24h count names
 * the wrong calendar day for part of every day, review R13F-2). The two
 * cannot clash on screen: the urgent banner never renders under a pay-now
 * strip (`isPayNowStrip`), whatever either count says.
 *
 * SERVER FIRST. `serverDaysLeft` is the offer's `days_left` (whole 24h periods
 * to the trial end, anchored to the server's clock - the same count behind
 * `is_last_day` and the discount ladder): when it is a number it decides. The
 * DEVICE clock is only the fallback for a payload that carries none, so a
 * wrong phone clock cannot show "3 days left" on trial day 2.
 *
 * @param {number|null|undefined} serverDaysLeft
 * @param {string|null|undefined} trialEndDate  the user's `trial_end_date`
 * @returns {number} 0 for past / invalid dates
 */
export const resolveTrialDaysLeft = (serverDaysLeft, trialEndDate) => {
  if (typeof serverDaysLeft === "number" && Number.isFinite(serverDaysLeft)) return serverDaysLeft;
  if (!trialEndDate) return 0;
  const diffMs = new Date(trialEndDate) - new Date();
  const diffDays = Math.floor(diffMs / MILLISECONDS_PER_DAY);
  return diffDays > 0 ? diffDays : 0;
};

/**
 * Israeli CALENDAR days from today to the user's REAL trial end, as the server
 * sent it (`trial_calendar_days_left`, computed with `app/core/timezones.py`):
 * 0 = the trial ends today, 1 = tomorrow. It follows `users.trial_end_date`,
 * so an admin extension moves it (review R13F-1) - unlike the offer's
 * `days_left` / `is_last_day`, which count 24h periods to the offer's own end.
 *
 * FAIL-CLOSED: anything but a whole number >= 0 (missing on an older backend,
 * null after the trial end, a string) answers null, and no surface may then
 * name a day.
 *
 * @param {object|null|undefined} offer  the `GET /payments/offer` payload
 * @returns {number|null}
 */
export const readTrialCalendarDaysLeft = (offer) => {
  const raw = offer?.trial_calendar_days_left;
  return Number.isInteger(raw) && raw >= 0 ? raw : null;
};

const NO_NEXT_TIER = Object.freeze({
  nextPercent: null,
  nextPercentText: null,
  nextChangeAt: null,
  isLastDay: false,
});

const finitePositive = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Tomorrow's step of the early-upgrade ladder, as the server sent it.
 *
 * Only an EARLY-UPGRADE offer has a ladder: a friend code or a win-back keeps
 * its percent, so for them (and for no offer at all) this answers "no next
 * tier" and no drops-daily sentence can be built.
 *
 * @param {object|null|undefined} offer  the `GET /payments/offer` payload
 * @returns {{nextPercent: number|null, nextPercentText: string|null,
 *   nextChangeAt: string|null, isLastDay: boolean}}
 *   `nextPercent` is null when the next step is the trial end; `isLastDay` is
 *   the server's "fewer than 24h remain".
 */
export const resolveNextTier = (offer) => {
  if (!offer || offer.eligible !== true || offer.discount_kind !== OFFER_KIND.EARLY_UPGRADE) {
    return NO_NEXT_TIER;
  }
  const isLastDay = offer.is_last_day === true;
  // On the last day there is no tomorrow, whatever else the payload says.
  const nextPercent = isLastDay ? null : finitePositive(offer.next_discount_percent);
  return {
    nextPercent,
    nextPercentText: nextPercent === null ? null : formatDiscountPercent(nextPercent),
    nextChangeAt: typeof offer.next_change_at === "string" ? offer.next_change_at : null,
    isLastDay,
  };
};

/**
 * Which "the discount drops" sentence a surface shows under its price: F3 on
 * the last day, F5 otherwise, or none.
 *
 * F5 states only THAT the discount drops (owner, ROUND 8 item 2 - never
 * tomorrow's figure), but it is still said only when it is TRUE: the server's
 * `next_discount_percent` is the proof that the next daily step really lowers
 * the percent, and it is null for a frozen percent (saved, then cancelled) or
 * when the next step is the trial end. No figure is shown; the field is the
 * gate.
 *
 * Read through the same offer view-model every selling surface switches on,
 * so a referred customer (whose percent never steps) can never be shown it.
 *
 * @param {object|null|undefined} offer
 * @param {object|null|undefined} user
 * @returns {{copyKey: "lastDay"|"dropsDaily", percentText: string}|null}
 */
export const resolveDropsDailyLine = (offer, user) => {
  if (!isCardOnFileLive(offer)) return null;
  const offerView = resolveOfferView(offer, user);
  if (offerView.kind !== OFFER_KIND.EARLY_UPGRADE) return null;
  const tier = resolveNextTier(offer);
  const { percentText } = offerView;
  if (tier.isLastDay) return { copyKey: "lastDay", percentText };
  if (tier.nextPercent === null) return null;
  return { copyKey: "dropsDaily", percentText };
};

/**
 * The strip for this user, or null when it must not render.
 *
 * WHO. A trialing, non-admin, self-serve (never invoice-billed) user, while
 * the card-on-file feature is live. Then, in priority order:
 *   1. a scheduled first charge -> nothing (ROUND 4: no strip once a card is
 *      saved - the charge notices speak for it);
 *   2. no live early-upgrade offer -> nothing (a friend-code user's percent
 *      does not step daily, and the strip's sentence is about that offer);
 *   3. the server promotes a card save, outside the offer's last day ->
 *      SAVE_CARD (F2 - unchanged in every window, its countdown is the save
 *      card's business);
 *   4. the trial ends TODAY (`readTrialCalendarDaysLeft` 0) -> LAST_DAY (F3,
 *      ROUND 13: "today the trial ends");
 *   5. it ends in 1-3 Israeli calendar days -> ENDING (ROUND 13: F1 carrying
 *      the trial-ending countdown - "tomorrow" is the next calendar day);
 *   6. otherwise -> PAY_NOW (F1) - also when the calendar count is missing
 *      (fail closed: no day is named), and on the OFFER's last day of an
 *      admin-extended trial, whose real end is further away (review R13F-1).
 * The trial-ending words are picked by the calendar count ONLY, never by the
 * offer's 24h `days_left` / `is_last_day`: those count to the offer's end
 * (the trial's first 14 days), and a 24h floor says "today" for a trial that
 * ends tomorrow morning (review R13F-2).
 * The strip states TODAY's percent only (ROUND 4 dropped its "tomorrow"
 * clause), so a FROZEN percent - a user who saved and then cancelled - still
 * gets its strip with that percent (review FE-3): it never needs a next tier.
 *
 * `dropsDaily` gates the F1 / F2 subline "the discount shrinks every day"
 * (ROUND 10) exactly as `resolveDropsDailyLine` gates F5: said only while the
 * server's `next_discount_percent` proves the next step really lowers the
 * percent - so a frozen percent gets its strip WITHOUT that claim. The figure
 * itself is never shown (ROUND 8). The ENDING and LAST_DAY states carry their
 * own subline ("scanning and the AI agents will stop") instead, whatever
 * `dropsDaily` says.
 *
 * `daysLeft` is the calendar count the ENDING / LAST_DAY headline is picked
 * by (0 on the last day); null on F1 / F2, whose words name no day.
 *
 * @param {object|null|undefined} offer
 * @param {object|null|undefined} user
 * @returns {{state: string, percentText: string, dropsDaily: boolean,
 *   daysLeft: number|null}|null}
 */
export const resolveTrialOfferStrip = (offer, user) => {
  if (!user || user.is_admin || user.status !== "trial") return null;
  if (isInvoiceBilledAccount(user)) return null;
  if (!isCardOnFileLive(offer)) return null;

  const cardOnFile = resolveCardOnFileView(offer);
  if (cardOnFile.scheduled) return null;

  const offerView = resolveOfferView(offer, user);
  if (offerView.kind !== OFFER_KIND.EARLY_UPGRADE || !offerView.percentText) return null;
  const { isLastDay, nextPercent } = resolveNextTier(offer);
  const calendarDays = readTrialCalendarDaysLeft(offer);
  let state = STRIP_STATE.PAY_NOW;
  let daysLeft = null;
  if (cardOnFile.promote && !isLastDay) {
    state = STRIP_STATE.SAVE_CARD;
  } else if (calendarDays === 0) {
    state = STRIP_STATE.LAST_DAY;
    daysLeft = 0;
  } else if (calendarDays !== null && calendarDays <= ENDING_WINDOW_DAYS) {
    state = STRIP_STATE.ENDING;
    daysLeft = calendarDays;
  }
  return {
    state,
    percentText: offerView.percentText,
    dropsDaily: !isLastDay && nextPercent !== null,
    daysLeft,
  };
};

/**
 * Does this user get the failed-first-charge banner (ROUND 6 item 5) - the
 * dashboard's ask for a new payment method while a scheduled first charge
 * sits in its grace days, shown INSTEAD of the strip (which a scheduled user
 * never gets anyway)?
 *
 * WHO. A trialing (only a trial can hold a schedule), non-admin, self-serve
 * user whose `card_on_file.scheduled.first_charge_failed` is true. Not gated
 * on the feature flag: a live schedule is always shown, whatever the flag says
 * (the server sends `scheduled` regardless), and its grace pauses the user's
 * opportunities either way.
 *
 * @param {object|null|undefined} offer
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const resolveFirstChargeFailedBanner = (offer, user) => {
  if (!user || user.is_admin || user.status !== "trial") return false;
  if (isInvoiceBilledAccount(user)) return false;
  return resolveCardOnFileView(offer).firstChargeFailed === true;
};
